import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { SeasonService, LeagueSeasonService, LeagueService } from '../../services/firebaseService';

export default function AddSeasonModal({ isOpen, onClose, leagueId, onSeasonAdded }) {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitationCode, setInvitationCode] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadActiveSeasons();
    }
  }, [isOpen]);

  const loadActiveSeasons = async () => {
    try {
      setLoading(true);
      const activeSeasons = await SeasonService.getActiveSeasons();
      setSeasons(activeSeasons);
    } catch (error) {
      console.error('Error loading active seasons:', error);
      setError('Failed to load active seasons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSeasonId) {
      setError('Please select a season');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Check if season is already added to this league and get existing seasons for copying scoring rules
      const existingSeasons = await LeagueService.getLeagueSeasons(leagueId);
      const seasonAlreadyExists = existingSeasons.some(season => season.seasonId === selectedSeasonId);
      
      if (seasonAlreadyExists) {
        setError('This season is already added to your league');
        setLoading(false);
        return;
      }

      // Scoring rules: copy from a previous season in the league, or use defaults (only these 4 fields)
      const defaultScoringRules = {
        postMergePredictionPoints: 3,
        preMergePredictionPoints: 3,
        survivalPoints: 1,
        winnerPoints: 1
      };
      const previousSeasons = existingSeasons.filter(s => s.seasonId !== selectedSeasonId);
      const sourceSeason = previousSeasons.length > 0 ? previousSeasons[previousSeasons.length - 1] : null;
      const scoringRules = sourceSeason?.scoringRules
        ? {
            postMergePredictionPoints: sourceSeason.scoringRules.postMergePredictionPoints ?? defaultScoringRules.postMergePredictionPoints,
            preMergePredictionPoints: sourceSeason.scoringRules.preMergePredictionPoints ?? defaultScoringRules.preMergePredictionPoints,
            survivalPoints: sourceSeason.scoringRules.survivalPoints ?? defaultScoringRules.survivalPoints,
            winnerPoints: sourceSeason.scoringRules.winnerPoints ?? defaultScoringRules.winnerPoints
          }
        : { ...defaultScoringRules };

      // Create the league season
      const seasonData = {
        seasonId: selectedSeasonId,
        draftStatus: 'notStarted',
        scoringRules,
        draftOrder: [],
        currentDraftRound: 1,
        currentDraftPick: 1,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await LeagueSeasonService.createLeagueSeason(leagueId, selectedSeasonId, seasonData);
      
      // Fetch the created season to get the invitation code
      const createdSeason = await LeagueSeasonService.getLeagueSeason(leagueId, selectedSeasonId);
      setInvitationCode(createdSeason.invitationCode);
      setSuccess(`Season ${selectedSeasonId} added successfully!`);
      
      // Call the callback to refresh the parent component
      if (onSeasonAdded) {
        onSeasonAdded();
      }
      
    } catch (error) {
      console.error('Error adding season to league:', error);
      setError('Failed to add season to league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedSeasonId('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Add Season to League</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm text-green-800 mb-2">{success}</p>
              {invitationCode && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-green-900">Invitation Code:</p>
                  <div className="mt-1 p-2 bg-green-100 border border-green-300 rounded font-mono text-lg font-bold text-green-900">
                    {invitationCode}
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    Share this code with players to join this season.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className={success ? 'pointer-events-none opacity-50' : ''}>
            <div className="mb-4">
              <label htmlFor="season" className="block text-sm font-medium text-gray-700 mb-2">
                Select Season
              </label>
              
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-survivor-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading seasons...</span>
                </div>
              ) : (
                <select
                  id="season"
                  value={selectedSeasonId}
                  onChange={(e) => setSelectedSeasonId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  required
                >
                  <option value="">Choose a season...</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name} ({season.year})
                    </option>
                  ))}
                </select>
              )}
              
              {seasons.length === 0 && !loading && (
                <p className="mt-2 text-sm text-gray-500">
                  No active seasons available. Contact an admin to create a new season.
                </p>
              )}
            </div>

            {/* Selected Season Info */}
            {selectedSeasonId && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex items-center">
                  <CalendarIcon className="w-5 h-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {seasons.find(s => s.id === selectedSeasonId)?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {seasons.find(s => s.id === selectedSeasonId)?.year}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              {success ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-survivor-600 border border-transparent rounded-md hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !selectedSeasonId}
                    className="px-4 py-2 text-sm font-medium text-white bg-survivor-600 border border-transparent rounded-md hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Adding...' : 'Add Season'}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
